import { useRef, useState } from 'react';
import { RecordControl } from './RecordControl';
import { TextEntry } from './TextEntry';
import { describeRecord } from '../model/record';
import type { ChoiceStep, FormEditor, FormField, FormValue, Scalar } from '../protocol/ui';
import type { FieldSchema } from '../protocol/wire';
import { usePresentation } from '../presentation/PresentationContext';
import { Slot } from './ObjectCard';
import { allocationBounds, initialAllocation, initialFormValue, switchFormVariant, validateFormValue, validateAllocation, type ParameterField, type StepValue } from '../model/choices';

type Pick = Extract<ChoiceStep, { type: 'pick' }>;
type Allocate = Extract<ChoiceStep, { type: 'allocate' }>;

export function describeAnswer(step: ChoiceStep, value: StepValue): string {
  if (step.type === 'record') return describeRecord(step.form, value as import('../protocol/ui').JsonValue);
  if (step.type === 'pick') {
    const values = Array.isArray(value) ? value : [value];
    return values.map(v => step.options.find(o => o.value === v)?.label ?? String(v)).join(', ');
  }
  if (step.type === 'allocate') {
    const parts = step.keys.map((k, i) => ({ k, v: (value as number[])[i] ?? 0 })).filter(x => x.v > 0).map(x => `${x.v} ${x.k.label}`);
    return parts.length ? parts.join(', ') : 'nothing';
  }
  if (step.type === 'form') return value === undefined ? 'Omitted' : summarizeForm(step.editor, value);
  return String(value);
}

interface StepProps { step: ChoiceStep; group: string; onAnswer: (value: StepValue) => void; onInspect: (id: string) => void }

export function StepControl({ step, group, onAnswer, onInspect }: StepProps) {
  const control = usePresentation().control(step, group);
  switch (step.type) {
    case 'pick': return (step.max ?? 1) === 1 && !step.ordered
      ? <PickOne step={step} onAnswer={onAnswer} onInspect={onInspect} compact={control === 'list'} inPlace={control === 'in-place' || step.options.every(o => !!o.objectId)} />
      : <PickMany step={step} onAnswer={onAnswer} />;
    case 'allocate': return <AllocateControl step={step} onAnswer={onAnswer} icons={control !== 'stepper'} />;
    case 'number': return <NumberControl step={step} onAnswer={onAnswer} />;
    case 'text': return <TextControl step={step} onAnswer={onAnswer} multiline={control === 'textarea'} />;
    case 'form': return <FormControl step={step} onAnswer={onAnswer} />;
    case 'record': return <RecordControl step={step} onAnswer={onAnswer} />;
  }
}

function summarizeForm(editor: FormEditor, value: FormValue): string {
  if (editor.type === 'array') return (value as FormValue[]).map(v => summarizeForm(editor.items, v)).join('; ') || 'Empty list';
  if (editor.type === 'object') {
    const object = value as Record<string, FormValue>;
    const variant = editor.discriminator?.variants.find(v => v.value === object[editor.discriminator!.field]);
    return [variant?.label, ...[...editor.fields, ...(variant?.fields ?? [])].filter(f => Object.hasOwn(object, f.field)).map(f => `${f.label}: ${summarizeForm(f.editor, object[f.field]!)}`)].filter(Boolean).join(', ') || 'Empty object';
  }
  return editor.options?.find(o => o.value === value)?.label ?? String(value);
}

function FormControl({ step, onAnswer }: { step: Extract<ChoiceStep, { type: 'form' }>; onAnswer: (v: StepValue) => void }) {
  const [value, setValue] = useState<FormValue | undefined>(() => step.optional ? undefined : initialFormValue(step.editor));
  const problem = value === undefined && step.optional ? null : validateFormValue(step.editor, value);
  return <fieldset className="step form-step">
    <legend>{step.label}</legend>
    {step.optional && <label className="check"><input type="checkbox" checked={value !== undefined} onChange={e => setValue(e.target.checked ? initialFormValue(step.editor) : undefined)} />Include {step.label}</label>}
    {value !== undefined && <FormEditorControl editor={step.editor} label={step.label} value={value} onChange={setValue} />}
    {problem && <p className="field-hint" role="status">{problem}</p>}
    <button type="button" className="button" disabled={!!problem} onClick={() => onAnswer(value)}>Continue</button>
  </fieldset>;
}

/** A bounded typed editor. Array keys are local identities and never enter command data. */
export function FormEditorControl({ editor, label, value, onChange }: { editor: FormEditor; label: string; value: FormValue; onChange: (v: FormValue) => void }) {
  const nextId = useRef(1);
  const ids = useRef<number[]>([]);
  if (editor.type === 'array') {
    const values = value as FormValue[];
    while (ids.current.length < values.length) ids.current.push(nextId.current++);
    ids.current.length = values.length;
    const move = (i: number, delta: number) => {
      const list = [...values], keys = [...ids.current], j = i + delta;
      if (j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j]!, list[i]!]; [keys[i], keys[j]] = [keys[j]!, keys[i]!]; ids.current = keys; onChange(list);
    };
    return <div className="form-array">
      <ol>{values.map((item, i) => <li key={ids.current[i]}>
        <FormEditorControl editor={editor.items} label={`${label} ${i + 1}`} value={item} onChange={v => onChange(values.map((x, j) => i === j ? v : x))} />
        <button type="button" className="icon-button" aria-label={`Move ${label} ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
        <button type="button" className="icon-button" aria-label={`Move ${label} ${i + 1} down`} disabled={i === values.length - 1} onClick={() => move(i, 1)}>↓</button>
        <button type="button" className="button button-quiet" aria-label={`Remove ${label} ${i + 1}`} disabled={values.length <= editor.minItems} onClick={() => { ids.current.splice(i, 1); onChange(values.filter((_, j) => i !== j)); }}>Remove</button>
      </li>)}</ol>
      <button type="button" className="button" disabled={values.length >= editor.maxItems} onClick={() => onChange([...values, initialFormValue(editor.items)])}>Add {label}</button>
    </div>;
  }
  if (editor.type === 'object') {
    const object = value as Record<string, FormValue>;
    const discriminator = editor.discriminator;
    const selected = discriminator?.variants.findIndex(v => v.value === object[discriminator.field]) ?? -1;
    const fields = [...editor.fields, ...(discriminator?.variants[selected]?.fields ?? [])];
    const setField = (f: FormField, v?: FormValue) => { const next = { ...object }; if (v === undefined) delete next[f.field]; else next[f.field] = v; onChange(next); };
    return <fieldset className="form-object"><legend>{label}</legend>
      {discriminator && <label className="param"><span>Type ({label})</span><select className="input" aria-label={`Type (${label})`} value={selected} onChange={e => onChange(switchFormVariant(editor, value, Number(e.target.value)))}>
        <option value={-1} disabled>Choose a type</option>{discriminator.variants.map((v, i) => <option key={i} value={i}>{v.label}</option>)}
      </select></label>}
      {fields.map(f => <div key={`${selected}:${f.field}`} className="form-field">
        {f.optional && <label className="check"><input type="checkbox" checked={Object.hasOwn(object, f.field)} onChange={e => setField(f, e.target.checked ? initialFormValue(f.editor) : undefined)} />Include {f.label}</label>}
        {Object.hasOwn(object, f.field) && <FormEditorControl editor={f.editor} label={f.label} value={object[f.field]!} onChange={v => setField(f, v)} />}
      </div>)}
    </fieldset>;
  }
  if (editor.options) return <label className="param"><span>{label}</span><select className="input" aria-label={label} value={editor.options.findIndex(o => o.value === value)} onChange={e => onChange(editor.options![Number(e.target.value)]!.value)}>
    {editor.options.map((o, i) => <option key={i} value={i}>{o.label}</option>)}
  </select></label>;
  if (editor.type === 'boolean') return <label className="check"><input type="checkbox" checked={value === true} onChange={e => onChange(e.target.checked)} />{label}</label>;
  if (editor.type === 'integer') return <label className="param"><span>{label}</span><input className="input input-number" type="number" min={editor.min} max={editor.max} aria-label={label} value={typeof value === 'number' && Number.isFinite(value) ? value : ''} onChange={e => onChange(e.target.value === '' ? NaN : Number(e.target.value))} /></label>;
  return <div className="param"><span>{label}</span><TextEntry label={label} maxLength={editor.maxLength} value={String(value)} onChange={onChange} /></div>;
}

function PickOne({ step, onAnswer, onInspect, compact, inPlace }: { step: Pick; onAnswer: (v: StepValue) => void; onInspect: (id: string) => void; compact: boolean; inPlace: boolean }) {
  const describe = usePresentation().describeSubject;
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const matches = step.options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const pages = Math.max(1, Math.ceil(matches.length / 100)), current = Math.min(page, pages - 1);
  return (
    <fieldset className="step">
      <legend>{step.label}</legend>
      {inPlace && <p className="field-hint">Choose on the table, or from this list.</p>}
      {step.options.length > 100 && <input className="input" aria-label={`Search ${step.label}`} placeholder="Search options…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />}
      <ul className={compact ? 'choice-list choice-list-compact' : 'choice-list'}>
        {matches.slice(current * 100, (current + 1) * 100).map(o => (
          <li key={String(o.value)}>
            <button type="button" className="choice-button" onClick={() => onAnswer(o.value)}
              onFocus={() => o.objectId && onInspect(o.objectId)}>
              {o.label}
              {o.objectId && describe?.(o.objectId) && <small className="choice-detail">{describe(o.objectId)}</small>}
            </button>
          </li>
        ))}
      </ul>
      {pages > 1 && <div className="record-actions"><button type="button" className="button" disabled={current === 0} onClick={() => setPage(current - 1)}>Previous options</button><span>Page {current + 1} of {pages}</span><button type="button" className="button" disabled={current + 1 >= pages} onClick={() => setPage(current + 1)}>Next options</button></div>}
      {(step.min ?? 1) === 0 && <button type="button" className="button" onClick={() => onAnswer([])}>Choose none</button>}
    </fieldset>
  );
}

function PickMany({ step, onAnswer }: { step: Pick; onAnswer: (v: StepValue) => void }) {
  const [picked, setPicked] = useState<Scalar[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const matches = step.options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const pages = Math.max(1, Math.ceil(matches.length / 100)), current = Math.min(page, pages - 1);
  const min = step.min ?? 1, max = step.max ?? 1;
  const toggle = (v: Scalar) => setPicked(p => (p.includes(v) ? p.filter(x => x !== v) : p.length < max ? [...p, v] : p));
  const move = (index: number, delta: number) => setPicked(p => {
    const next = [...p]; const target = index + delta;
    if (target < 0 || target >= next.length) return p;
    [next[index], next[target]] = [next[target]!, next[index]!];
    return next;
  });
  const label = (v: Scalar) => step.options.find(o => o.value === v)?.label ?? String(v);
  return (
    <fieldset className="step">
      <legend>{step.label} <span className="muted">({min === max ? `choose ${min}` : `choose ${min} to ${max}`})</span></legend>
      {step.options.length > 100 && <input className="input" aria-label={`Search ${step.label}`} placeholder="Search options…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />}
      <ul className="check-list">
        {matches.slice(current * 100, (current + 1) * 100).map(o => (
          <li key={String(o.value)}>
            <label className="check"><input type="checkbox" checked={picked.includes(o.value)} onChange={() => toggle(o.value)}
              disabled={!picked.includes(o.value) && picked.length >= max} />{o.label}</label>
          </li>
        ))}
      </ul>
      {pages > 1 && <div className="record-actions"><button type="button" className="button" disabled={!current} onClick={() => setPage(current - 1)}>Previous options</button><span>Page {current + 1} of {pages}</span><button type="button" className="button" disabled={current + 1 === pages} onClick={() => setPage(current + 1)}>Next options</button></div>}
      {!!picked.length && !step.ordered && <p className="field-hint">Selected: {picked.map(label).join(', ')}</p>}
      {step.ordered && picked.length > 1 && (
        <ol className="order-list" aria-label="Order">
          {picked.map((v, i) => (
            <li key={String(v)}>
              <span>{label(v)}</span>
              <button type="button" className="icon-button" aria-label={`Move ${label(v)} up`} onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
              <button type="button" className="icon-button" aria-label={`Move ${label(v)} down`} onClick={() => move(i, 1)} disabled={i === picked.length - 1}>↓</button>
            </li>
          ))}
        </ol>
      )}
      <button type="button" className="button" disabled={picked.length < min} onClick={() => onAnswer(step.ordered ? picked : step.options.map(o => o.value).filter(v => picked.includes(v)))}>
        Continue
      </button>
    </fieldset>
  );
}

function AllocateControl({ step, onAnswer, icons }: { step: Allocate; onAnswer: (v: StepValue) => void; icons: boolean }) {
  const api = usePresentation();
  const [values, setValues] = useState<number[]>(() => initialAllocation(step));
  const total = values.reduce((a, b) => a + b, 0);
  const problem = validateAllocation(step, values);
  const set = (i: number, v: number) => setValues(vs => vs.map((x, j) => (j === i ? v : x)));
  return (
    <fieldset className="step">
      <legend>{step.label}</legend>
      <div className="allocate">
        {step.keys.map((k, i) => {
          const { min, max } = allocationBounds(k);
          const v = values[i] ?? 0;
          return (
            <div key={k.key} className="allocate-row">
              <span className="allocate-label">{icons && api.icon(k.key) && <span className="counter-icon" aria-hidden="true"><Slot value={api.icon(k.key)!} /></span>}{k.label}{k.available !== undefined && <span className="muted"> (have {k.available})</span>}</span>
              <button type="button" className="icon-button" aria-label={`Less ${k.label}`} onClick={() => set(i, v - 1)} disabled={v <= min}>−</button>
              <input className="input input-number" type="number" inputMode="numeric" aria-label={k.label} min={min} max={max} value={v}
                onChange={e => set(i, Number.parseInt(e.target.value || '0', 10))} />
              <button type="button" className="icon-button" aria-label={`More ${k.label}`} onClick={() => set(i, v + 1)} disabled={v >= max}>+</button>
            </div>
          );
        })}
      </div>
      {step.sum && <p className={`allocate-total${problem ? ' is-off' : ''}`}>Total {total}{step.sum.min === step.sum.max ? ` of ${step.sum.min}` : ` (needs ${step.sum.min} to ${step.sum.max})`}</p>}
      {problem && <p className="field-hint">{problem}</p>}
      <button type="button" className="button" disabled={!!problem} onClick={() => onAnswer(values)}>Continue</button>
    </fieldset>
  );
}

function NumberControl({ step, onAnswer }: { step: Extract<ChoiceStep, { type: 'number' }>; onAnswer: (v: StepValue) => void }) {
  const [value, setValue] = useState(step.min);
  const ok = Number.isInteger(value) && value >= step.min && value <= step.max;
  return (
    <fieldset className="step">
      <legend>{step.label}</legend>
        <input className="input input-number" type="number" min={step.min} max={step.max} value={value} aria-label={step.label}
          onChange={e => setValue(Number.parseInt(e.target.value || '0', 10))}
          onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing && ok) { e.preventDefault(); e.stopPropagation(); onAnswer(value); } }} />
      <button type="button" className="button" disabled={!ok} onClick={() => onAnswer(value)}>Continue</button>
    </fieldset>
  );
}

export function TextControl({ step, onAnswer, multiline = false }: { step: Extract<ChoiceStep, { type: 'text' }>; onAnswer: (v: StepValue) => void; multiline?: boolean }) {
  const [value, setValue] = useState('');
  return (
    <fieldset className="step">
      <legend>{step.label}</legend>
      <TextEntry label={step.label} maxLength={step.maxLength} value={value} onChange={setValue} multiline={multiline} onAdvance={() => onAnswer(value)} />
      <button type="button" className="button" onClick={() => onAnswer(value)}>Continue</button>
    </fieldset>
  );
}

/** ui-1 fallback: edit the fields a legal action marks as `parameters`, typed by the action schema. */
export function ParameterForm({ fields, values, onChange }: { fields: ParameterField[]; values: Record<string, unknown>; onChange: (name: string, value: unknown) => void }) {
  return (
    <fieldset className="step">
      <legend>Adjust</legend>
      {fields.map(f => <ParameterInput key={f.name} field={f} value={values[f.name]} onChange={v => onChange(f.name, v)} />)}
    </fieldset>
  );
}

function ParameterInput({ field, value, onChange }: { field: ParameterField; value: unknown; onChange: (v: unknown) => void }) {
  const s: FieldSchema = field.schema;
  if (s.type === 'integer') {
    return <label className="param"><span>{field.label}</span><input className="input input-number" type="number" min={s.minimum ?? undefined} max={s.maximum ?? undefined}
      value={Number(value ?? 0)} onChange={e => onChange(Number.parseInt(e.target.value || '0', 10))} /></label>;
  }
  if (s.type === 'boolean') {
    return <label className="check"><input type="checkbox" checked={Boolean(value)} onChange={e => onChange(e.target.checked)} />{field.label}</label>;
  }
  if (s.type === 'string' && s.values?.length) {
    return <label className="param"><span>{field.label}</span><select className="input" value={String(value ?? '')} onChange={e => onChange(e.target.value)}>
      {s.values.map(v => <option key={v} value={v}>{v}</option>)}</select></label>;
  }
  if (s.type === 'string') {
    return <div className="param"><span>{field.label}</span><TextEntry label={field.label} maxLength={s.maxLength ?? undefined} value={String(value ?? '')} onChange={onChange} /></div>;
  }
  if (s.type === 'array' && s.items?.type === 'integer' && Array.isArray(value)) {
    const list = value as number[];
    return (
      <div className="param">
        <span>{field.label}</span>
        <div className="param-array">
          {list.map((v, i) => (
            <input key={i} className="input input-number" type="number" aria-label={`${field.label} ${i + 1}`} min={s.items?.minimum ?? undefined} max={s.items?.maximum ?? undefined}
              value={v} onChange={e => onChange(list.map((x, j) => (j === i ? Number.parseInt(e.target.value || '0', 10) : x)))} />
          ))}
        </div>
      </div>
    );
  }
  return <p className="muted">{field.label}: {JSON.stringify(value)}</p>;
}
